import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  ArrowLeft,
  Crown,
  Scale,
  MessageSquare,
  Utensils,
  Search,
  User,
  Phone,
  Mail,
  Target,
  Check,
  AlertCircle,
  Edit3,
  ClipboardList,
  Dumbbell,
  Apple,
  Heart,
  FileText,
  Camera,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Save,
  Plus,
  Trash2,
  Calculator,
  Flame,
  TrendingUp,
  TrendingDown,
  Bookmark,
  BookmarkPlus,
  History,
  Settings2,
  Salad,
  ChefHat,
  Loader2,
  Library,
  Zap
} from "lucide-react";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import AdvancedMealEditor, { calculateTotalMacros } from "../../components/admin/AdvancedMealEditor";
import MacroSuggestions from "../../components/admin/MacroSuggestions";
import DietHistory from "../../components/admin/DietHistory";
import ClientCheckInsView from "../../components/admin/ClientCheckInsView";

// Helper: Busca un alimento en foodsLibrary por nombre (case-insensitive, partial match)
const findFoodByName = (name, foodsLibrary) => {
  if (!name || !foodsLibrary) return null;
  
  const normalizedName = name.toLowerCase().trim();
  
  // First pass: exact match
  for (const category in foodsLibrary) {
    const foods = foodsLibrary[category] || [];
    for (const food of foods) {
      if (food.name.toLowerCase() === normalizedName) {
        return { ...food, matchedCategory: category };
      }
    }
  }
  
  // Second pass: partial match (starts with or contains)
  for (const category in foodsLibrary) {
    const foods = foodsLibrary[category] || [];
    for (const food of foods) {
      const foodNameLower = food.name.toLowerCase();
      if (foodNameLower.includes(normalizedName) || normalizedName.includes(foodNameLower)) {
        return { ...food, matchedCategory: category };
      }
    }
  }
  
  return null;
};

// Legacy name to DB name mapping for better matching
const LEGACY_NAME_MAPPING = {
  'claras': 'Claras de huevo',
  'claras de huevo': 'Claras de huevo',
  'huevos_enteros': 'Huevos enteros',
  'huevos enteros': 'Huevos enteros',
  'pechuga_pollo': 'Pechuga de pollo',
  'pechuga de pollo': 'Pechuga de pollo',
  'pollo': 'Pechuga de pollo',
  'pavo': 'Pavo',
  'pavo_lonchas': 'Pavo en lonchas',
  'ternera': 'Ternera magra',
  'salmon': 'Salmón',
  'merluza': 'Merluza',
  'atun_lata': 'Atún en lata',
  'atun': 'Atún en lata',
  'avena': 'Avena',
  'arroz_basmati': 'Arroz basmati',
  'arroz': 'Arroz blanco',
  'patata': 'Patata',
  'boniato': 'Boniato',
  'pan_integral': 'Pan integral',
  'tortitas_arroz': 'Tortitas de arroz',
  'proteina_whey': 'Proteína whey',
  'whey': 'Proteína whey',
  'canela': 'Canela',
  'miel': 'Miel',
  'mantequilla_cacahuete': 'Mantequilla de cacahuete',
  'crema_cacahuete': 'Crema de cacahuete',
  'aceite_oliva': 'Aceite de oliva',
  'aguacate': 'Aguacate',
  'almendras': 'Almendras',
  'nueces': 'Nueces',
  'yogur_griego': 'Yogur griego 0%',
  'queso_fresco': 'Queso fresco batido 0%',
  'manzana': 'Manzana',
  'platano': 'Plátano',
  'frutos_rojos': 'Frutos rojos mix',
  'ensalada_mixta': 'Ensalada mixta',
  'brocoli': 'Brócoli',
  'espinacas': 'Espinacas',
  'champinones': 'Champiñones',
  'esparragos': 'Espárragos',
  'tomate': 'Tomate',
  'dorada': 'Dorada',
};

// Normalize legacy food items to use correct DB UUIDs
const normalizeLegacyItems = (items, foodsLibrary) => {
  if (!items || !foodsLibrary || Object.keys(foodsLibrary).length === 0) {
    return items;
  }
  
  return items.map(item => {
    // If item already has a valid UUID (36 chars with dashes), it's probably already correct
    if (item.food_id && item.food_id.length === 36 && item.food_id.includes('-')) {
      return item;
    }
    
    // Try to find the food by legacy ID -> name mapping
    const legacyId = item.food_id?.toLowerCase();
    const dbName = LEGACY_NAME_MAPPING[legacyId];
    
    if (dbName) {
      const dbFood = findFoodByName(dbName, foodsLibrary);
      if (dbFood) {
        return {
          ...item,
          food_id: dbFood.id,
          category: dbFood.category || item.category,
          unit: item.unit || dbFood.unit || 'g'
        };
      }
    }
    
    // If custom_name exists, try to find by that
    if (item.custom_name) {
      const dbFood = findFoodByName(item.custom_name, foodsLibrary);
      if (dbFood) {
        return {
          ...item,
          food_id: dbFood.id,
          category: dbFood.category,
          custom_name: undefined // Clear since we found a match
        };
      }
    }
    
    // No match found - mark as custom/manual entry
    return {
      ...item,
      category: 'custom',
      food_id: 'custom',
      custom_name: item.custom_name || LEGACY_NAME_MAPPING[legacyId] || item.food_id || 'Alimento sin identificar'
    };
  });
};

// Función para convertir texto legacy a estructura de items
const parseLegacyMealToItems = (text) => {
  if (!text) return [];
  
  const items = [];
  const parts = text.split(/\s*\+\s*/);
  
  // Mapeo de palabras clave a IDs de alimentos con unidades preferidas
  // IMPORTANTE: Las palabras más largas/específicas tienen prioridad
  const foodMapping = {
    // Proteínas
    'claras': { id: 'claras', category: 'proteinas', preferUnit: 'g', multiplier: 30 }, // 1 clara ≈ 30g
    'huevos enteros revueltos': { id: 'huevos_enteros', category: 'proteinas', preferUnit: 'uds' },
    'huevos enteros': { id: 'huevos_enteros', category: 'proteinas', preferUnit: 'uds' },
    'huevos': { id: 'huevos_enteros', category: 'proteinas', preferUnit: 'uds' },
    'huevo': { id: 'huevos_enteros', category: 'proteinas', preferUnit: 'uds' },
    'pechuga de pollo': { id: 'pechuga_pollo', category: 'proteinas', preferUnit: 'g' },
    'pollo': { id: 'pechuga_pollo', category: 'proteinas', preferUnit: 'g' },
    'pechuga': { id: 'pechuga_pollo', category: 'proteinas', preferUnit: 'g' },
    'pavo en lonchas': { id: 'pavo_lonchas', category: 'proteinas', preferUnit: 'g' },
    'pavo': { id: 'pavo', category: 'proteinas', preferUnit: 'g' },
    'ternera magra': { id: 'ternera', category: 'proteinas', preferUnit: 'g' },
    'ternera': { id: 'ternera', category: 'proteinas', preferUnit: 'g' },
    'salmón': { id: 'salmon', category: 'proteinas', preferUnit: 'g' },
    'salmon': { id: 'salmon', category: 'proteinas', preferUnit: 'g' },
    'merluza': { id: 'merluza', category: 'proteinas', preferUnit: 'g' },
    'dorada': { id: 'dorada', category: 'proteinas', preferUnit: 'g' },
    'atún al natural': { id: 'atun_lata', category: 'proteinas', preferUnit: 'g' },
    'atún': { id: 'atun_lata', category: 'proteinas', preferUnit: 'g' },
    'atun': { id: 'atun_lata', category: 'proteinas', preferUnit: 'g' },
    // Carbohidratos (más específicos primero)
    'avena (en crudo)': { id: 'avena', category: 'carbohidratos', preferUnit: 'g' },
    'copos de avena': { id: 'avena', category: 'carbohidratos', preferUnit: 'g' },
    'avena': { id: 'avena', category: 'carbohidratos', preferUnit: 'g' },
    'arroz basmati (en crudo)': { id: 'arroz_basmati', category: 'carbohidratos', preferUnit: 'g' },
    'arroz basmati': { id: 'arroz_basmati', category: 'carbohidratos', preferUnit: 'g' },
    'arroz': { id: 'arroz_basmati', category: 'carbohidratos', preferUnit: 'g' },
    'patata cocida': { id: 'patata', category: 'carbohidratos', preferUnit: 'g' },
    'patata': { id: 'patata', category: 'carbohidratos', preferUnit: 'g' },
    'boniato': { id: 'boniato', category: 'carbohidratos', preferUnit: 'g' },
    'pan integral tostado': { id: 'pan_integral', category: 'carbohidratos', preferUnit: 'g' },
    'pan integral': { id: 'pan_integral', category: 'carbohidratos', preferUnit: 'g' },
    'pan': { id: 'pan_integral', category: 'carbohidratos', preferUnit: 'g' },
    'tortitas de arroz': { id: 'tortitas_arroz', category: 'carbohidratos', preferUnit: 'uds' },
    'tortitas': { id: 'tortitas_arroz', category: 'carbohidratos', preferUnit: 'uds' },
    // Suplementos
    'proteína whey': { id: 'proteina_whey', category: 'suplementos', preferUnit: 'g' },
    'proteina whey': { id: 'proteina_whey', category: 'suplementos', preferUnit: 'g' },
    'whey': { id: 'proteina_whey', category: 'suplementos', preferUnit: 'g' },
    'canela': { id: 'canela', category: 'suplementos', preferUnit: 'g' },
    'miel': { id: 'miel', category: 'suplementos', preferUnit: 'g' },
    // Grasas
    'mantequilla de cacahuete': { id: 'mantequilla_cacahuete', category: 'grasas', preferUnit: 'g' },
    'crema de cacahuete': { id: 'crema_cacahuete', category: 'grasas', preferUnit: 'g' },
    'aceite oliva': { id: 'aceite_oliva', category: 'grasas', preferUnit: 'g' },
    'aceite de oliva': { id: 'aceite_oliva', category: 'grasas', preferUnit: 'g' },
    'aceite': { id: 'aceite_oliva', category: 'grasas', preferUnit: 'g' },
    'aguacate': { id: 'aguacate', category: 'grasas', preferUnit: 'g' },
    'almendras': { id: 'almendras', category: 'grasas', preferUnit: 'g' },
    'nueces': { id: 'nueces', category: 'grasas', preferUnit: 'g' },
    // Lácteos
    'yogur griego': { id: 'yogur_griego', category: 'lacteos', preferUnit: 'g' },
    'yogur': { id: 'yogur_griego', category: 'lacteos', preferUnit: 'g' },
    'queso fresco batido': { id: 'queso_fresco', category: 'lacteos', preferUnit: 'g' },
    'queso fresco': { id: 'queso_fresco', category: 'lacteos', preferUnit: 'g' },
    'queso': { id: 'queso_fresco', category: 'lacteos', preferUnit: 'g' },
    // Frutas
    'pieza de fruta': { id: 'manzana', category: 'frutas', preferUnit: 'uds' },
    'frutos rojos': { id: 'frutos_rojos', category: 'frutas', preferUnit: 'g' },
    'fruta': { id: 'manzana', category: 'frutas', preferUnit: 'uds' },
    'manzana': { id: 'manzana', category: 'frutas', preferUnit: 'uds' },
    'plátano': { id: 'platano', category: 'frutas', preferUnit: 'uds' },
    'platano': { id: 'platano', category: 'frutas', preferUnit: 'uds' },
    // Verduras
    'verduras salteadas': { id: 'ensalada_mixta', category: 'verduras', preferUnit: 'g' },
    'verduras ilimitadas': { id: 'ensalada_mixta', category: 'verduras', preferUnit: 'g' },
    'verduras': { id: 'ensalada_mixta', category: 'verduras', preferUnit: 'g' },
    'ensalada grande': { id: 'ensalada_mixta', category: 'verduras', preferUnit: 'g' },
    'ensalada': { id: 'ensalada_mixta', category: 'verduras', preferUnit: 'g' },
    'brócoli': { id: 'brocoli', category: 'verduras', preferUnit: 'g' },
    'brocoli': { id: 'brocoli', category: 'verduras', preferUnit: 'g' },
    'espinacas': { id: 'espinacas', category: 'verduras', preferUnit: 'g' },
    'champiñones': { id: 'champinones', category: 'verduras', preferUnit: 'g' },
    'champinones': { id: 'champinones', category: 'verduras', preferUnit: 'g' },
    'espárragos': { id: 'esparragos', category: 'verduras', preferUnit: 'g' },
    'esparragos': { id: 'esparragos', category: 'verduras', preferUnit: 'g' },
    'tomate': { id: 'tomate', category: 'verduras', preferUnit: 'g' },
  };
  
  parts.forEach((part, idx) => {
    // Patrón mejorado para detectar "4 claras", "60g avena", "1 pieza de fruta", etc.
    const match = part.match(/(\d+)\s*(g|ml|uds|unidades|claras?|huevos?|pieza|lata|cda)?\s*(.+)/i);
    if (match) {
      let amount = parseInt(match[1]) || 100;
      let unitHint = (match[2] || '').toLowerCase();
      const foodText = match[3].toLowerCase().trim();
      
      // Buscar el alimento (primero frases largas, luego cortas)
      let found = null;
      const sortedKeys = Object.keys(foodMapping).sort((a, b) => b.length - a.length);
      
      for (const keyword of sortedKeys) {
        if (foodText.includes(keyword) || part.toLowerCase().includes(keyword)) {
          found = { ...foodMapping[keyword] };
          break;
        }
      }
      
      if (found) {
        // Determinar unidad y cantidad
        let finalUnit = found.preferUnit || 'g';
        let finalAmount = amount;
        
        // Si el hint es "claras", convertir a gramos (1 clara ≈ 30g)
        if (unitHint === 'claras' || unitHint === 'clara') {
          if (found.multiplier) {
            finalAmount = amount * found.multiplier;
            finalUnit = 'g';
          }
        }
        // Si el hint es "huevos" o "huevo", mantener como unidades
        else if (unitHint === 'huevos' || unitHint === 'huevo') {
          finalUnit = 'uds';
        }
        // Si el hint es "g", usar gramos
        else if (unitHint === 'g') {
          finalUnit = 'g';
        }
        // Si el hint es "cda" (cucharada), convertir a gramos
        else if (unitHint === 'cda') {
          finalAmount = amount * 10;
          finalUnit = 'g';
        }
        // Si el hint es "pieza", usar unidades
        else if (unitHint === 'pieza' || unitHint === 'lata' || unitHint === 'unidades' || unitHint === 'uds') {
          finalUnit = 'uds';
        }
        
        items.push({
          id: `${Date.now()}-${idx}-${Math.random().toString(36).substr(2, 9)}`,
          category: found.category,
          food_id: found.id,
          amount: finalAmount,
          unit: finalUnit
        });
      }
    }
  });
  
  return items;
};

// Convertir comida legacy a nuevo formato con items
const convertLegacyMealToNew = (meal) => {
  if (meal.items_a && meal.items_a.length > 0) {
    // Ya tiene el nuevo formato
    return meal;
  }
  
  return {
    ...meal,
    items_a: parseLegacyMealToItems(meal.option_a),
    items_b: parseLegacyMealToItems(meal.option_b),
  };
};

// Convertir items estructurados de vuelta a texto legible para el cliente
const convertItemsToText = (items, foodsLibrary = {}) => {
  if (!items || items.length === 0) return "";
  
  // Flatten all foods from library for lookup
  const allFoods = [];
  for (const category in foodsLibrary) {
    allFoods.push(...(foodsLibrary[category] || []));
  }
  
  return items.map(item => {
    // Find food by ID in library
    const food = allFoods.find(f => f.id === item.food_id);
    const foodName = food ? food.name : (item.custom_name || item.food_id || 'Alimento');
    const unit = item.unit || 'g';
    
    if (unit === 'uds') {
      return `${item.amount} ${foodName}`;
    } else {
      return `${item.amount}g ${foodName} (en crudo)`;
    }
  }).join(" + ");
};

// Convertir comida con items de vuelta al formato legacy para guardar
const convertMealToLegacy = (meal, foodsLibrary = {}) => {
  // Si tiene items_a o items_b, convertirlos a texto
  const option_a = meal.items_a && meal.items_a.length > 0 
    ? convertItemsToText(meal.items_a, foodsLibrary) 
    : meal.option_a || "";
  
  const option_b = meal.items_b && meal.items_b.length > 0 
    ? convertItemsToText(meal.items_b, foodsLibrary) 
    : meal.option_b || "";
  
  return {
    name: meal.name,
    time: meal.time,
    option_a: option_a,
    option_b: option_b,
    notes: meal.notes || ""
  };
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPremiumClients = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [clients, setClients] = useState([]);
  const [diets, setDiets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showDietDialog, setShowDietDialog] = useState(false);
  const [showDietEditor, setShowDietEditor] = useState(false);
  const [selectedDietId, setSelectedDietId] = useState("");
  const [savingDiet, setSavingDiet] = useState(false);
  const [editingDiet, setEditingDiet] = useState(null);
  const [coachNotes, setCoachNotes] = useState("");
  const [foodsLibrary, setFoodsLibrary] = useState({});
  const [mealTemplates, setMealTemplates] = useState([]);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [savingMealAsTemplate, setSavingMealAsTemplate] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [historyNote, setHistoryNote] = useState("");
  
  // Flexible Diet System State
  const [nutritionConfig, setNutritionConfig] = useState({
    diet_mode: "traditional",
    objetivo: "recomposicion",
    daily_targets: { calories: 2000, protein: 150, carbs: 200, fat: 70 },
    meal_slots: {}
  });
  const [savingNutritionConfig, setSavingNutritionConfig] = useState(false);
  const [showNutritionConfigEditor, setShowNutritionConfigEditor] = useState(false);
  
  // Recipe Seed State (temporal para poblar recetas en producción)
  const [seedingRecipes, setSeedingRecipes] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  
  // GOAT Library - Plantillas rápidas
  const [nutritionTemplates, setNutritionTemplates] = useState([]);
  const [workoutTemplates, setWorkoutTemplates] = useState([]);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  
  // Modal GOAT Completo (Entrenamiento + Nutrición)
  const [showGoatWizard, setShowGoatWizard] = useState(false);
  const [goatWizardStep, setGoatWizardStep] = useState(1); // 1: Workout, 2: Nutrition, 3: Review
  const [selectedWorkoutTemplate, setSelectedWorkoutTemplate] = useState(null);
  const [selectedNutritionTemplate, setSelectedNutritionTemplate] = useState(null);
  const [applyingGoat, setApplyingGoat] = useState(false);
  const [wizardGenderFilter, setWizardGenderFilter] = useState("");
  const [wizardObjectiveFilter, setWizardObjectiveFilter] = useState("");

  // Meal slot distribution percentages - DEFAULT 5 meals
  const DEFAULT_MEAL_DISTRIBUTION = {
    desayuno: { percent: 20, name: "Desayuno" },
    media_manana: { percent: 15, name: "Media Mañana" },
    comida: { percent: 30, name: "Comida" },
    merienda: { percent: 15, name: "Merienda" },
    cena: { percent: 20, name: "Cena" }
  };

  // Plantillas de distribución de comidas
  const MEAL_TEMPLATES = {
    4: {
      name: "4 Comidas",
      slots: {
        desayuno: { percent: 25, name: "Desayuno" },
        comida: { percent: 35, name: "Comida" },
        merienda: { percent: 15, name: "Merienda" },
        cena: { percent: 25, name: "Cena" }
      }
    },
    5: {
      name: "5 Comidas",
      slots: {
        desayuno: { percent: 20, name: "Desayuno" },
        media_manana: { percent: 15, name: "Media Mañana" },
        comida: { percent: 30, name: "Comida" },
        merienda: { percent: 15, name: "Merienda" },
        cena: { percent: 20, name: "Cena" }
      }
    },
    6: {
      name: "6 Comidas",
      slots: {
        desayuno: { percent: 20, name: "Desayuno" },
        media_manana: { percent: 10, name: "Media Mañana" },
        comida: { percent: 30, name: "Comida" },
        merienda: { percent: 10, name: "Merienda" },
        cena: { percent: 20, name: "Cena" },
        recena: { percent: 10, name: "Antes de Dormir" }
      }
    }
  };
  
  // Estado para la distribución personalizada de comidas
  const [mealDistribution, setMealDistribution] = useState(MEAL_TEMPLATES[5].slots);
  const [selectedTemplate, setSelectedTemplate] = useState(5);

  // Load foods library for macro calculations
  const loadFoodsLibrary = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/foods/active`, {
        headers: getAuthHeaders()
      });
      setFoodsLibrary(response.data.by_category || {});
    } catch (error) {
      console.error("Error loading foods library:", error);
    }
  }, [getAuthHeaders]);

  // Load meal templates
  const loadMealTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/meal-templates`, {
        headers: getAuthHeaders()
      });
      setMealTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error loading meal templates:", error);
    }
  }, [getAuthHeaders]);

  // Seed all recipes to database (one-time production setup)
  const handleSeedRecipes = async () => {
    if (seedingRecipes) return;
    
    const confirmed = window.confirm(
      "¿Ejecutar seed de recetas?\n\nEsto añadirá/actualizará las 81 recetas fitness (59 base + 22 trendy) en la base de datos.\n\nEs seguro ejecutar múltiples veces."
    );
    
    if (!confirmed) return;
    
    setSeedingRecipes(true);
    setSeedResult(null);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/seed-all-recipes`,
        {},
        { headers: getAuthHeaders() }
      );
      setSeedResult(response.data);
      toast.success(`✅ ${response.data.total_recipes} recetas en base de datos`);
    } catch (error) {
      console.error("Error seeding recipes:", error);
      toast.error("Error al ejecutar seed de recetas");
      setSeedResult({ error: error.message });
    } finally {
      setSeedingRecipes(false);
    }
  };

  // Calculate daily totals from all meals
  const dailyTotals = useMemo(() => {
    if (!editingDiet?.meals || !foodsLibrary) {
      return { optionA: { calories: 0, protein: 0, carbs: 0, fat: 0 }, optionB: { calories: 0, protein: 0, carbs: 0, fat: 0 } };
    }

    const totals = editingDiet.meals.reduce((acc, meal) => {
      const itemsA = meal.items_a || [];
      const itemsB = meal.items_b || [];
      const mealTotalsA = calculateTotalMacros(itemsA, foodsLibrary);
      const mealTotalsB = calculateTotalMacros(itemsB, foodsLibrary);
      
      return {
        optionA: {
          calories: acc.optionA.calories + mealTotalsA.calories,
          protein: Math.round((acc.optionA.protein + mealTotalsA.protein) * 10) / 10,
          carbs: Math.round((acc.optionA.carbs + mealTotalsA.carbs) * 10) / 10,
          fat: Math.round((acc.optionA.fat + mealTotalsA.fat) * 10) / 10
        },
        optionB: {
          calories: acc.optionB.calories + mealTotalsB.calories,
          protein: Math.round((acc.optionB.protein + mealTotalsB.protein) * 10) / 10,
          carbs: Math.round((acc.optionB.carbs + mealTotalsB.carbs) * 10) / 10,
          fat: Math.round((acc.optionB.fat + mealTotalsB.fat) * 10) / 10
        }
      };
    }, { optionA: { calories: 0, protein: 0, carbs: 0, fat: 0 }, optionB: { calories: 0, protein: 0, carbs: 0, fat: 0 } });

    return totals;
  }, [editingDiet?.meals, foodsLibrary]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/premium-clients`, {
        headers: getAuthHeaders(),
      });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  const fetchDiets = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/diets`, {
        headers: getAuthHeaders(),
      });
      setDiets(response.data || []);
    } catch (error) {
      console.error("Error fetching diets:", error);
    }
  };
  
  // Cargar plantillas nutricionales GOAT
  const fetchNutritionTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/nutrition-templates`, {
        headers: getAuthHeaders(),
      });
      setNutritionTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching nutrition templates:", error);
    }
  }, [getAuthHeaders]);
  
  // Cargar plantillas de entrenamiento
  const fetchWorkoutTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/custom-programs/templates`, {
        headers: getAuthHeaders(),
      });
      setWorkoutTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching workout templates:", error);
    }
  }, [getAuthHeaders]);

  // Main useEffect - load all data
  useEffect(() => {
    fetchClients();
    fetchDiets();
    loadFoodsLibrary();
    loadMealTemplates();
    fetchNutritionTemplates();
    fetchWorkoutTemplates();
  }, [loadFoodsLibrary, loadMealTemplates, fetchNutritionTemplates, fetchWorkoutTemplates]);
  
  // Aplicar plantilla nutricional a cliente
  const applyNutritionTemplate = async (templateId) => {
    if (!selectedClient) return;
    
    setApplyingTemplate(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/members/${selectedClient.user.id}/apply-nutrition-template/${templateId}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success(response.data.message || "Plantilla aplicada");
      setShowApplyTemplate(false);
      fetchClientDetail(selectedClient.user.id);
    } catch (error) {
      console.error("Error applying template:", error);
      toast.error("Error al aplicar plantilla");
    } finally {
      setApplyingTemplate(false);
    }
  };
  
  // =============================================
  // GOAT WIZARD - Aplicar Entrenamiento + Nutrición
  // =============================================
  const openGoatWizard = () => {
    setSelectedWorkoutTemplate(null);
    setSelectedNutritionTemplate(null);
    setGoatWizardStep(1);
    setWizardGenderFilter("");
    setWizardObjectiveFilter("");
    setShowGoatWizard(true);
  };
  
  const applyGoatConfig = async () => {
    if (!selectedClient) return;
    
    setApplyingGoat(true);
    const results = { workout: null, nutrition: null };
    
    try {
      // 1. Aplicar entrenamiento si hay selección
      if (selectedWorkoutTemplate) {
        const workoutRes = await axios.post(
          `${API_URL}/api/custom-programs/${selectedWorkoutTemplate.id}/assign/${selectedClient.user.id}`,
          {},
          { headers: getAuthHeaders() }
        );
        results.workout = workoutRes.data;
      }
      
      // 2. Aplicar nutrición si hay selección
      if (selectedNutritionTemplate) {
        const nutritionRes = await axios.post(
          `${API_URL}/api/admin/members/${selectedClient.user.id}/apply-nutrition-template/${selectedNutritionTemplate.id}`,
          {},
          { headers: getAuthHeaders() }
        );
        results.nutrition = nutritionRes.data;
      }
      
      // Mostrar resumen de éxito
      const appliedItems = [];
      if (results.workout) appliedItems.push("Entrenamiento");
      if (results.nutrition) appliedItems.push("Nutrición");
      
      toast.success(`✅ Configuración GOAT aplicada: ${appliedItems.join(" + ")}`);
      setShowGoatWizard(false);
      fetchClientDetail(selectedClient.user.id);
      
    } catch (error) {
      console.error("Error applying GOAT config:", error);
      toast.error("Error al aplicar configuración GOAT");
    } finally {
      setApplyingGoat(false);
    }
  };

  const fetchClientDetail = async (clientId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/premium-clients/${clientId}`, {
        headers: getAuthHeaders(),
      });
      setClientDetail(response.data);
      setCoachNotes(response.data.profile?.notas_admin || "");
      
      // Load nutrition config (diet_mode, objetivo) from profile
      const profile = response.data.profile || {};
      const dietMode = profile.diet_mode || "traditional";
      const objetivo = profile.objetivo || "recomposicion";
      
      // Load flexible plan if exists
      try {
        const flexPlanRes = await axios.get(
          `${API_URL}/api/admin/members/${clientId}/flexible-plan`,
          { headers: getAuthHeaders() }
        );
        
        if (flexPlanRes.data.has_flexible_plan && flexPlanRes.data.plan) {
          const plan = flexPlanRes.data.plan;
          setNutritionConfig({
            diet_mode: dietMode,
            objetivo: objetivo,
            daily_targets: plan.daily_targets || { calories: 2000, protein: 150, carbs: 200, fat: 70 },
            meal_slots: plan.meal_slots || {}
          });
          
          // Load meal distribution from plan if exists
          if (plan.meal_slots && Object.keys(plan.meal_slots).length > 0) {
            const distribution = {};
            for (const [slotId, slotConfig] of Object.entries(plan.meal_slots)) {
              // Calculate percent from targets
              const slotCals = slotConfig.targets?.calories || 0;
              const totalCals = plan.daily_targets?.calories || 1;
              const percent = Math.round((slotCals / totalCals) * 100);
              distribution[slotId] = {
                name: slotConfig.name || slotId,
                percent: percent
              };
            }
            setMealDistribution(distribution);
            setSelectedTemplate(Object.keys(distribution).length);
          }
        } else {
          // Use diet macros if available
          const diet = response.data.diet;
          setNutritionConfig({
            diet_mode: dietMode,
            objetivo: objetivo,
            daily_targets: {
              calories: diet?.calories_target || 2000,
              protein: diet?.protein_target || 150,
              carbs: diet?.carbs_target || 200,
              fat: diet?.fat_target || 70
            },
            meal_slots: {}
          });
          // Reset to default distribution
          setMealDistribution(MEAL_TEMPLATES[5].slots);
          setSelectedTemplate(5);
        }
      } catch (err) {
        // No flexible plan, use traditional diet values
        const diet = response.data.diet;
        setNutritionConfig({
          diet_mode: dietMode,
          objetivo: objetivo,
          daily_targets: {
            calories: diet?.calories_target || 2000,
            protein: diet?.protein_target || 150,
            carbs: diet?.carbs_target || 200,
            fat: diet?.fat_target || 70
          },
          meal_slots: {}
        });
        // Reset to default distribution
        setMealDistribution(MEAL_TEMPLATES[5].slots);
        setSelectedTemplate(5);
      }
    } catch (error) {
      console.error("Error fetching client detail:", error);
      toast.error("Error al cargar detalles");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    fetchClientDetail(client.user.id);
  };

  const handleAssignDiet = async () => {
    if (!selectedClient || !selectedDietId) return;
    
    setSavingDiet(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/members/${selectedClient.user.id}/assign-diet`,
        { diet_id: selectedDietId },
        { headers: getAuthHeaders() }
      );
      toast.success("Dieta asignada correctamente");
      setShowDietDialog(false);
      fetchClientDetail(selectedClient.user.id);
      fetchClients();
    } catch (error) {
      toast.error("Error al asignar dieta");
    } finally {
      setSavingDiet(false);
    }
  };

  const handleOpenDietEditor = () => {
    if (clientDetail?.diet) {
      // Convert meals to new format with items
      const convertedMeals = (clientDetail.diet.meals || []).map(convertLegacyMealToNew);
      
      // Normalize legacy items to use correct DB UUIDs
      const normalizedMeals = convertedMeals.map(meal => ({
        ...meal,
        items_a: normalizeLegacyItems(meal.items_a || [], foodsLibrary),
        items_b: normalizeLegacyItems(meal.items_b || [], foodsLibrary)
      }));
      
      // Create editable copy of diet
      setEditingDiet({
        calories_target: clientDetail.diet.calories_target,
        protein_target: clientDetail.diet.protein_target,
        carbs_target: clientDetail.diet.carbs_target,
        fat_target: clientDetail.diet.fat_target,
        meals: JSON.parse(JSON.stringify(normalizedMeals)),
        sustituciones: clientDetail.diet.sustituciones || "",
        suplementacion: clientDetail.diet.suplementacion || "",
        notas_goatgym: clientDetail.diet.notas_goatgym || "",
        ajustes_estancamiento: clientDetail.diet.ajustes_estancamiento || "",
        coach_notes: clientDetail.client_diet?.coach_notes || ""
      });
      setShowDietEditor(true);
    }
  };

  const handleSaveClientDiet = async () => {
    if (!selectedClient || !editingDiet) return;
    
    setSavingDiet(true);
    try {
      // Convert meals with items back to legacy text format for the client view
      const mealsForSave = (editingDiet.meals || []).map(m => convertMealToLegacy(m, foodsLibrary));
      
      const dietToSave = {
        ...editingDiet,
        meals: mealsForSave,
        history_note: historyNote || undefined  // Include note for history
      };
      
      // Use the new endpoint that records history
      await axios.put(
        `${API_URL}/api/admin/members/${selectedClient.user.id}/client-diet-with-history`,
        dietToSave,
        { headers: getAuthHeaders() }
      );
      toast.success("Dieta del cliente actualizada");
      setShowDietEditor(false);
      setHistoryNote("");  // Clear note
      fetchClientDetail(selectedClient.user.id);
    } catch (error) {
      // Fallback to old endpoint if new one fails
      try {
        const mealsForSave = (editingDiet.meals || []).map(m => convertMealToLegacy(m, foodsLibrary));
        const dietToSave = { ...editingDiet, meals: mealsForSave };
        
        await axios.put(
          `${API_URL}/api/admin/members/${selectedClient.user.id}/client-diet`,
          dietToSave,
          { headers: getAuthHeaders() }
        );
        toast.success("Dieta actualizada (sin historial)");
        setShowDietEditor(false);
        fetchClientDetail(selectedClient.user.id);
      } catch (e) {
        console.error("Error saving diet:", e);
        toast.error("Error al guardar dieta");
      }
    } finally {
      setSavingDiet(false);
    }
  };

  // Duplicate a meal
  const handleDuplicateMeal = (mealIndex) => {
    if (!editingDiet?.meals) return;
    
    const mealToCopy = editingDiet.meals[mealIndex];
    const duplicatedMeal = {
      ...JSON.parse(JSON.stringify(mealToCopy)),
      name: `${mealToCopy.name} (copia)`,
      items_a: (mealToCopy.items_a || []).map(item => ({
        ...item,
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random()
      })),
      items_b: (mealToCopy.items_b || []).map(item => ({
        ...item,
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random()
      }))
    };
    
    const newMeals = [...editingDiet.meals];
    newMeals.splice(mealIndex + 1, 0, duplicatedMeal);
    setEditingDiet({ ...editingDiet, meals: newMeals });
    toast.success("Comida duplicada");
  };

  // Save a meal as template
  const handleSaveMealAsTemplate = async () => {
    if (!savingMealAsTemplate || !templateName.trim()) return;
    
    const meal = savingMealAsTemplate;
    const macrosA = calculateTotalMacros(meal.items_a || [], foodsLibrary);
    const macrosB = calculateTotalMacros(meal.items_b || [], foodsLibrary);
    
    try {
      await axios.post(
        `${API_URL}/api/admin/meal-templates`,
        {
          name: templateName.trim(),
          time: meal.time,
          items_a: meal.items_a || [],
          items_b: meal.items_b || [],
          notes: meal.notes || "",
          macros_a: macrosA,
          macros_b: macrosB
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Plantilla guardada");
      setShowTemplateDialog(false);
      setSavingMealAsTemplate(null);
      setTemplateName("");
      loadMealTemplates();
    } catch (error) {
      toast.error("Error al guardar plantilla");
    }
  };

  // Load a template into the diet
  const handleLoadTemplate = (template) => {
    if (!editingDiet) return;
    
    const newMeal = {
      name: template.name,
      time: template.time || "12:00",
      items_a: (template.items_a || []).map(item => ({
        ...item,
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random()
      })),
      items_b: (template.items_b || []).map(item => ({
        ...item,
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random()
      })),
      notes: template.notes || ""
    };
    
    setEditingDiet({
      ...editingDiet,
      meals: [...(editingDiet.meals || []), newMeal]
    });
    toast.success(`Plantilla "${template.name}" añadida`);
  };

  // Apply macro suggestion
  const handleApplySuggestion = (suggestion) => {
    if (!editingDiet?.meals || editingDiet.meals.length === 0) {
      toast.error("Añade al menos una comida primero");
      return;
    }

    const { food, amount, type } = suggestion;
    
    if (type === 'add') {
      // Add the food to the first meal's Option A
      const newItem = {
        id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
        category: food.category,
        food_id: food.id,
        amount: amount,
        unit: food.unit || 'g'
      };
      
      const newMeals = [...editingDiet.meals];
      // Find the best meal to add to (prefer meals with fewer items or matching category)
      let targetMealIdx = 0;
      
      // Try to find a meal that already has foods in this category
      for (let i = 0; i < newMeals.length; i++) {
        const mealItems = newMeals[i].items_a || [];
        if (mealItems.some(item => item.category === food.category)) {
          targetMealIdx = i;
          break;
        }
      }
      
      newMeals[targetMealIdx] = {
        ...newMeals[targetMealIdx],
        items_a: [...(newMeals[targetMealIdx].items_a || []), newItem]
      };
      
      setEditingDiet({ ...editingDiet, meals: newMeals });
      toast.success(`+${amount}${food.unit || 'g'} ${food.name} añadido a ${newMeals[targetMealIdx].name}`);
    } else {
      // Reduce: find and adjust the food in existing meals
      let found = false;
      const newMeals = editingDiet.meals.map(meal => {
        if (found) return meal;
        
        const itemsA = (meal.items_a || []).map(item => {
          if (found) return item;
          if (item.food_id === food.id && item.amount >= amount) {
            found = true;
            const newAmount = item.amount - amount;
            if (newAmount <= 5) {
              // Remove item if amount becomes too small
              return null;
            }
            return { ...item, amount: newAmount };
          }
          return item;
        }).filter(Boolean);
        
        return { ...meal, items_a: itemsA };
      });
      
      if (found) {
        setEditingDiet({ ...editingDiet, meals: newMeals });
        toast.success(`-${amount}${food.unit || 'g'} ${food.name} reducido`);
      } else {
        toast.info(`No se encontró ${food.name} para reducir. Ajusta manualmente.`);
      }
    }
  };

  // Get all food items from all meals (for suggestion prioritization)
  const getAllMealItems = useMemo(() => {
    if (!editingDiet?.meals) return [];
    
    return editingDiet.meals.flatMap(meal => [
      ...(meal.items_a || []),
      ...(meal.items_b || [])
    ]);
  }, [editingDiet?.meals]);

  const handleResetDiet = async () => {
    if (!selectedClient) return;
    
    if (!window.confirm("¿Resetear dieta al template original? Se perderán las personalizaciones.")) {
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/admin/members/${selectedClient.user.id}/reset-diet`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Dieta reseteada al template original");
      fetchClientDetail(selectedClient.user.id);
    } catch (error) {
      toast.error("Error al resetear dieta");
    }
  };

  const handleSaveCoachNotes = async () => {
    if (!selectedClient) return;
    
    try {
      await axios.put(
        `${API_URL}/api/admin/premium-clients/${selectedClient.user.id}/notes`,
        { nota: coachNotes },
        { headers: getAuthHeaders() }
      );
      toast.success("Notas guardadas");
    } catch (error) {
      toast.error("Error al guardar notas");
    }
  };

  // Save unified nutrition configuration
  const handleSaveNutritionConfig = async () => {
    if (!selectedClient) return;
    
    // Validar que los porcentajes sumen 100%
    if (nutritionConfig.diet_mode !== "traditional" && totalPercent !== 100) {
      toast.error(`Los porcentajes deben sumar 100%. Actualmente: ${totalPercent}%`);
      return;
    }
    
    // Validar macros básicos
    if (nutritionConfig.daily_targets.calories < 1000) {
      toast.error("Las calorías deben ser al menos 1000 kcal");
      return;
    }
    if (nutritionConfig.daily_targets.protein < 50) {
      toast.error("La proteína debe ser al menos 50g");
      return;
    }
    
    setSavingNutritionConfig(true);
    try {
      // Usar la distribución personalizada de comidas
      let mealSlots = {};
      if (nutritionConfig.diet_mode !== "traditional") {
        for (const [slotId, config] of Object.entries(mealDistribution)) {
          const pct = config.percent / 100;
          mealSlots[slotId] = {
            name: config.name,
            mode: "flexible",
            targets: {
              calories: Math.round(nutritionConfig.daily_targets.calories * pct),
              protein: Math.round(nutritionConfig.daily_targets.protein * pct),
              carbs: Math.round(nutritionConfig.daily_targets.carbs * pct),
              fat: Math.round(nutritionConfig.daily_targets.fat * pct)
            }
          };
        }
      }
      
      await axios.post(
        `${API_URL}/api/admin/members/${selectedClient.user.id}/nutrition-config`,
        {
          diet_mode: nutritionConfig.diet_mode,
          objetivo: nutritionConfig.objetivo,
          daily_targets: nutritionConfig.daily_targets,
          meal_slots: mealSlots,
          tolerance_percent: 5
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Configuración nutricional actualizada (${nutritionConfig.diet_mode})`);
      setShowNutritionConfigEditor(false);
      fetchClientDetail(selectedClient.user.id);
    } catch (error) {
      console.error("Error saving nutrition config:", error);
      toast.error("Error al guardar configuración nutricional");
    } finally {
      setSavingNutritionConfig(false);
    }
  };

  // Recalculate meal slots when daily targets change
  const recalculateMealSlots = (newDailyTargets, distribution = mealDistribution) => {
    const newMealSlots = {};
    for (const [slotId, config] of Object.entries(distribution)) {
      const pct = config.percent / 100;
      newMealSlots[slotId] = {
        name: config.name,
        mode: "flexible",
        targets: {
          calories: Math.round(newDailyTargets.calories * pct),
          protein: Math.round(newDailyTargets.protein * pct),
          carbs: Math.round(newDailyTargets.carbs * pct),
          fat: Math.round(newDailyTargets.fat * pct)
        }
      };
    }
    return newMealSlots;
  };
  
  // Calcular suma de porcentajes
  const totalPercent = useMemo(() => {
    return Object.values(mealDistribution).reduce((sum, slot) => sum + (slot.percent || 0), 0);
  }, [mealDistribution]);
  
  // Aplicar plantilla de comidas
  const applyMealTemplate = (numMeals) => {
    const template = MEAL_TEMPLATES[numMeals];
    if (template) {
      setSelectedTemplate(numMeals);
      setMealDistribution(JSON.parse(JSON.stringify(template.slots)));
      // Recalcular meal_slots con la nueva distribución
      if (nutritionConfig.diet_mode !== "traditional" && nutritionConfig.daily_targets) {
        setNutritionConfig(prev => ({
          ...prev,
          meal_slots: recalculateMealSlots(prev.daily_targets, template.slots)
        }));
      }
    }
  };
  
  // Actualizar porcentaje de una comida
  const updateMealPercent = (slotId, newPercent) => {
    const newDistribution = {
      ...mealDistribution,
      [slotId]: { ...mealDistribution[slotId], percent: Math.max(0, Math.min(100, parseInt(newPercent) || 0)) }
    };
    setMealDistribution(newDistribution);
    // Recalcular meal_slots
    if (nutritionConfig.diet_mode !== "traditional" && nutritionConfig.daily_targets) {
      setNutritionConfig(prev => ({
        ...prev,
        meal_slots: recalculateMealSlots(prev.daily_targets, newDistribution)
      }));
    }
  };
  
  // Actualizar nombre de una comida
  const updateMealName = (slotId, newName) => {
    const newDistribution = {
      ...mealDistribution,
      [slotId]: { ...mealDistribution[slotId], name: newName }
    };
    setMealDistribution(newDistribution);
    if (nutritionConfig.diet_mode !== "traditional" && nutritionConfig.daily_targets) {
      setNutritionConfig(prev => ({
        ...prev,
        meal_slots: recalculateMealSlots(prev.daily_targets, newDistribution)
      }));
    }
  };
  
  // Añadir nueva comida
  const addMealSlot = () => {
    const slotCount = Object.keys(mealDistribution).length;
    const newSlotId = `comida_${slotCount + 1}`;
    const newDistribution = {
      ...mealDistribution,
      [newSlotId]: { percent: 0, name: `Comida ${slotCount + 1}` }
    };
    setMealDistribution(newDistribution);
    setSelectedTemplate(null);
  };
  
  // Eliminar una comida
  const removeMealSlot = (slotId) => {
    if (Object.keys(mealDistribution).length <= 2) {
      toast.error("Mínimo 2 comidas requeridas");
      return;
    }
    const newDistribution = { ...mealDistribution };
    delete newDistribution[slotId];
    setMealDistribution(newDistribution);
    setSelectedTemplate(null);
    if (nutritionConfig.diet_mode !== "traditional" && nutritionConfig.daily_targets) {
      setNutritionConfig(prev => ({
        ...prev,
        meal_slots: recalculateMealSlots(prev.daily_targets, newDistribution)
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_review: { label: "Pendiente", color: "bg-yellow-500/10 text-yellow-400" },
      active_plan: { label: "Activo", color: "bg-green-500/10 text-green-400" },
      needs_update: { label: "Actualizar", color: "bg-orange-500/10 text-orange-400" },
      checkin_pending: { label: "Check-in", color: "bg-blue-500/10 text-blue-400" },
    };
    const badge = badges[status] || badges.pending_review;
    return <Badge className={`${badge.color} border-none text-xs`}>{badge.label}</Badge>;
  };

  const filteredClients = clients.filter((c) => {
    const search = searchTerm.toLowerCase();
    return (
      c.user?.email?.toLowerCase().includes(search) ||
      c.profile?.nombre?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        <p className="text-[#666] mt-4">Cargando clientes...</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in pb-24" data-testid="admin-premium-clients">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate("/admin")} className="p-2 rounded-xl hover:bg-[#1A1A1A] transition-colors">
          <ArrowLeft className="w-5 h-5 text-[#888]" />
        </button>
        <div>
          <h1 className="text-2xl font-heading font-black text-white flex items-center gap-2">
            <Crown className="w-6 h-6 text-[#D4AF37]" />
            Clientes Premium
          </h1>
          <p className="text-[#666] text-sm">{clients.length} clientes activos</p>
        </div>
      </div>

      {/* Seed Recipes Button - Temporal para producción */}
      <div className="mb-6 p-4 bg-[#1A1A1A] rounded-xl border border-[#2A2A2A]">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Utensils className="w-4 h-4 text-[#D4AF37]" />
              Recetas Flexible
            </h3>
            <p className="text-[#666] text-sm">
              {seedResult 
                ? `✅ ${seedResult.total_recipes} recetas (${seedResult.trendy_recipes} trendy)`
                : "Añadir/actualizar recetas fitness en la base de datos"
              }
            </p>
          </div>
          <Button
            onClick={handleSeedRecipes}
            disabled={seedingRecipes}
            className="bg-[#D4AF37] hover:bg-[#C4A030] text-black font-bold"
            data-testid="seed-recipes-btn"
          >
            {seedingRecipes ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Ejecutando...
              </>
            ) : (
              <>
                <ChefHat className="w-4 h-4 mr-2" />
                Seed Recetas
              </>
            )}
          </Button>
        </div>
        {seedResult && !seedResult.error && (
          <div className="mt-3 grid grid-cols-5 gap-2 text-center text-xs">
            {Object.entries(seedResult.by_category || {}).map(([cat, count]) => (
              <div key={cat} className="bg-[#0B0B0B] rounded p-2">
                <p className="text-[#D4AF37] font-bold">{count}</p>
                <p className="text-[#666] capitalize">{cat.replace('_', ' ')}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client List */}
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
            <Input
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-[#121212] border-[#2A2A2A] text-white pl-10"
            />
          </div>

          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredClients.map((client) => (
              <Card
                key={client.user.id}
                className={cn(
                  "bg-[#121212] border-[#2A2A2A] cursor-pointer transition-all",
                  selectedClient?.user.id === client.user.id
                    ? "border-[#D4AF37] bg-[#D4AF37]/5"
                    : "hover:border-[#3A3A3A]"
                )}
                onClick={() => handleSelectClient(client)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">
                        {client.profile?.nombre || client.user.email.split("@")[0]}
                      </p>
                      <p className="text-[#666] text-sm truncate">{client.user.email}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {getStatusBadge(client.status)}
                      {client.has_diet ? (
                        <Badge variant="outline" className="border-green-500/30 text-green-400 text-xs">
                          <Utensils className="w-3 h-3 mr-1" />
                          Dieta
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-orange-500/30 text-orange-400 text-xs">
                          Sin dieta
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Client Detail Panel */}
        <div>
          {!selectedClient ? (
            <Card className="bg-[#121212] border-[#2A2A2A] h-[400px] flex items-center justify-center">
              <div className="text-center">
                <User className="w-16 h-16 text-[#333] mx-auto mb-4" />
                <p className="text-[#666]">Selecciona un cliente para ver detalles</p>
              </div>
            </Card>
          ) : loadingDetail ? (
            <Card className="bg-[#121212] border-[#2A2A2A] h-[400px] flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            </Card>
          ) : clientDetail ? (
            <Card className="bg-[#121212] border-[#2A2A2A]">
              <CardContent className="p-4">
                <Tabs defaultValue="intake" className="w-full">
                  <TabsList className="grid grid-cols-5 bg-[#1A1A1A] p-1 rounded-lg mb-4">
                    <TabsTrigger value="intake" className="text-xs data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
                      <ClipboardList className="w-4 h-4" />
                    </TabsTrigger>
                    <TabsTrigger value="dieta" className="text-xs data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
                      <Utensils className="w-4 h-4" />
                    </TabsTrigger>
                    <TabsTrigger value="checkins" className="text-xs data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
                      <Scale className="w-4 h-4" />
                    </TabsTrigger>
                    <TabsTrigger value="fotos" className="text-xs data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
                      <Camera className="w-4 h-4" />
                    </TabsTrigger>
                    <TabsTrigger value="notas" className="text-xs data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0B0B0B]">
                      <MessageSquare className="w-4 h-4" />
                    </TabsTrigger>
                  </TabsList>

                  {/* INTAKE TAB - Full Onboarding Form View */}
                  <TabsContent value="intake" className="space-y-4 max-h-[500px] overflow-y-auto">
                    {/* Header */}
                    <div className="flex items-center gap-4 pb-4 border-b border-[#2A2A2A]">
                      <div className="w-14 h-14 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                        <User className="w-7 h-7 text-[#D4AF37]" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-lg font-bold text-white">
                          {clientDetail.profile?.nombre || clientDetail.user.email.split("@")[0]}
                        </h2>
                        <p className="text-[#888] text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {clientDetail.user.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(clientDetail.profile?.status)}
                        <Button
                          size="sm"
                          onClick={openGoatWizard}
                          className="bg-gradient-to-r from-[#D4AF37] to-[#B8960C] hover:from-[#E5C54D] hover:to-[#D4AF37] text-black font-bold shadow-lg"
                          data-testid="goat-wizard-btn"
                        >
                          <Zap className="w-4 h-4 mr-1" />
                          Plantilla GOAT
                        </Button>
                      </div>
                    </div>

                    {/* Basic Info Section */}
                    <IntakeSection title="Datos Básicos" icon={User}>
                      <IntakeField label="Peso Actual" value={`${clientDetail.profile?.peso_actual || "-"} kg`} />
                      <IntakeField label="Altura" value={`${clientDetail.profile?.altura || "-"} cm`} />
                      <IntakeField label="Edad" value={`${clientDetail.profile?.edad || "-"} años`} />
                      <IntakeField label="Género" value={clientDetail.profile?.sexo || "-"} />
                      <IntakeField label="Peso Inicial" value={`${clientDetail.profile?.peso_inicial || "-"} kg`} />
                      <IntakeField label="Peso Objetivo" value={clientDetail.profile?.peso_objetivo ? `${clientDetail.profile.peso_objetivo} kg` : "-"} />
                    </IntakeSection>

                    {/* Goal Section */}
                    <IntakeSection title="Objetivo" icon={Target}>
                      <IntakeField label="Objetivo Principal" value={formatGoal(clientDetail.profile?.objetivo)} highlight />
                      <IntakeField label="Motivación" value={clientDetail.profile?.motivacion || "-"} fullWidth />
                    </IntakeSection>

                    {/* Training Section */}
                    <IntakeSection title="Entrenamiento" icon={Dumbbell}>
                      <IntakeField label="Experiencia" value={formatExperience(clientDetail.profile?.nivel)} />
                      <IntakeField label="Días Disponibles" value={clientDetail.profile?.dias_entreno ? `${clientDetail.profile.dias_entreno} días/semana` : "-"} />
                      <IntakeField label="Equipo" value={formatEquipment(clientDetail.profile?.equipo_disponible)} />
                      <IntakeField label="Lesiones" value={clientDetail.profile?.lesiones || "Ninguna"} fullWidth />
                    </IntakeSection>

                    {/* Nutrition Section */}
                    <IntakeSection title="Nutrición" icon={Apple}>
                      <IntakeField label="Alergias" value={clientDetail.profile?.alergias || "Ninguna"} />
                      <IntakeField label="Comidas/día" value={formatMealSchedule(clientDetail.profile?.horario_comidas)} />
                      <IntakeField label="NO le gusta" value={clientDetail.profile?.comidas_no_gustan || "-"} fullWidth />
                      <IntakeField label="Favoritos" value={clientDetail.profile?.comidas_favoritas || "-"} fullWidth />
                      <IntakeField label="Suplementos" value={clientDetail.profile?.suplementos_actuales || "Ninguno"} fullWidth />
                    </IntakeSection>

                    {/* Lifestyle Section */}
                    <IntakeSection title="Estilo de Vida" icon={Heart}>
                      <IntakeField label="Nivel Estrés" value={clientDetail.profile?.nivel_estres ? `${clientDetail.profile.nivel_estres}/10` : "-"} />
                      <IntakeField label="Horas Sueño" value={clientDetail.profile?.horas_sueno ? `${clientDetail.profile.horas_sueno}h` : "-"} />
                      <IntakeField label="Actividad" value={formatActivity(clientDetail.profile?.nivel_actividad)} />
                      <IntakeField label="Trabajo" value={formatWork(clientDetail.profile?.tipo_trabajo)} />
                    </IntakeSection>

                    {/* Stats */}
                    <IntakeSection title="Estadísticas" icon={FileText}>
                      <IntakeField label="Entrenamientos" value={clientDetail.workout_stats?.total_completed || 0} />
                      <IntakeField label="Fecha Inicio" value={formatDate(clientDetail.profile?.fecha_inicio)} />
                      <IntakeField label="Onboarding" value={clientDetail.profile?.onboarding_completed ? "Completado" : "Pendiente"} />
                    </IntakeSection>
                  </TabsContent>

                  {/* DIETA TAB - Diet Assignment & Editing */}
                  <TabsContent value="dieta" className="space-y-4 max-h-[500px] overflow-y-auto">
                    {/* NUTRITION SYSTEM CONFIG - NEW */}
                    <Card className="bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 border-[#D4AF37]/30">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-[#D4AF37] font-semibold flex items-center gap-2">
                            <Settings2 className="w-4 h-4" />
                            Sistema Nutricional
                          </h3>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowApplyTemplate(true)}
                              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                            >
                              <Zap className="w-3 h-3 mr-1" />
                              Plantilla
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowNutritionConfigEditor(true)}
                              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                            >
                              <Edit3 className="w-3 h-3 mr-1" />
                              Manual
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3">
                          {/* Diet Mode */}
                          <div className="p-2 bg-[#1A1A1A] rounded-lg">
                            <p className="text-[#666] text-xs mb-1">Tipo de Dieta</p>
                            <Badge className={cn(
                              "border-none text-xs",
                              nutritionConfig.diet_mode === "traditional" && "bg-blue-500/10 text-blue-400",
                              nutritionConfig.diet_mode === "flexible" && "bg-green-500/10 text-green-400",
                              nutritionConfig.diet_mode === "hybrid" && "bg-purple-500/10 text-purple-400"
                            )}>
                              {nutritionConfig.diet_mode === "traditional" && "Tradicional"}
                              {nutritionConfig.diet_mode === "flexible" && "Flexible"}
                              {nutritionConfig.diet_mode === "hybrid" && "Híbrido"}
                            </Badge>
                          </div>
                          
                          {/* Objetivo */}
                          <div className="p-2 bg-[#1A1A1A] rounded-lg">
                            <p className="text-[#666] text-xs mb-1">Objetivo</p>
                            <Badge className={cn(
                              "border-none text-xs",
                              nutritionConfig.objetivo === "definicion" && "bg-red-500/10 text-red-400",
                              nutritionConfig.objetivo === "recomposicion" && "bg-yellow-500/10 text-yellow-400",
                              nutritionConfig.objetivo === "musculacion" && "bg-green-500/10 text-green-400"
                            )}>
                              {nutritionConfig.objetivo === "definicion" && "Definición"}
                              {nutritionConfig.objetivo === "recomposicion" && "Recomposición"}
                              {nutritionConfig.objetivo === "musculacion" && "Musculación"}
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Daily Targets Summary */}
                        <div className="grid grid-cols-4 gap-2 mt-3">
                          <div className="text-center p-2 bg-[#1A1A1A] rounded">
                            <p className="text-white font-bold text-sm">{nutritionConfig.daily_targets?.calories || 0}</p>
                            <p className="text-[#666] text-[10px]">kcal</p>
                          </div>
                          <div className="text-center p-2 bg-[#1A1A1A] rounded">
                            <p className="text-red-400 font-bold text-sm">{nutritionConfig.daily_targets?.protein || 0}g</p>
                            <p className="text-[#666] text-[10px]">Prot</p>
                          </div>
                          <div className="text-center p-2 bg-[#1A1A1A] rounded">
                            <p className="text-yellow-400 font-bold text-sm">{nutritionConfig.daily_targets?.carbs || 0}g</p>
                            <p className="text-[#666] text-[10px]">Carbs</p>
                          </div>
                          <div className="text-center p-2 bg-[#1A1A1A] rounded">
                            <p className="text-blue-400 font-bold text-sm">{nutritionConfig.daily_targets?.fat || 0}g</p>
                            <p className="text-[#666] text-[10px]">Grasas</p>
                          </div>
                        </div>
                        
                        {/* Info about mode */}
                        <div className="mt-3 p-2 bg-[#0B0B0B] rounded-lg">
                          <p className="text-[#888] text-xs">
                            {nutritionConfig.diet_mode === "traditional" && (
                              <>
                                <Utensils className="w-3 h-3 inline mr-1" />
                                El cliente ve comidas fijas definidas por el coach.
                              </>
                            )}
                            {nutritionConfig.diet_mode === "flexible" && (
                              <>
                                <ChefHat className="w-3 h-3 inline mr-1" />
                                El cliente elige recetas y el sistema calcula gramos automáticamente.
                              </>
                            )}
                            {nutritionConfig.diet_mode === "hybrid" && (
                              <>
                                <Salad className="w-3 h-3 inline mr-1" />
                                Mezcla: algunas comidas fijas + otras flexibles con recetas.
                              </>
                            )}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Traditional Diet Section - Only show if mode is traditional or hybrid */}
                    {(nutritionConfig.diet_mode === "traditional" || nutritionConfig.diet_mode === "hybrid") && (
                      <>
                        {clientDetail.diet ? (
                          <>
                            <Card className="bg-green-500/10 border-green-500/30">
                              <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h3 className="text-green-400 font-semibold flex items-center gap-2">
                                    <Check className="w-4 h-4" />
                                    Dieta Tradicional Asignada
                                  </h3>
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => {
                                        setSelectedDietId(clientDetail.diet.id);
                                        setShowDietDialog(true);
                                      }}
                                      className="border-[#2A2A2A] text-[#888] hover:text-white"
                                    >
                                      Cambiar
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={handleOpenDietEditor}
                                      className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
                                    >
                                      <Edit3 className="w-4 h-4 mr-1" />
                                      Editar
                                    </Button>
                                  </div>
                                </div>
                                <h4 className="text-white font-bold text-lg">{clientDetail.diet.name}</h4>
                                {clientDetail.diet.is_customized && (
                                  <Badge className="bg-purple-500/10 text-purple-400 border-none text-xs mt-1">
                                    Personalizada
                                  </Badge>
                                )}
                              </CardContent>
                            </Card>

                            {/* Meals Preview */}
                            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
                              <CardContent className="p-4">
                                <h4 className="text-white font-semibold mb-3">Comidas ({clientDetail.diet.meals?.length || 0})</h4>
                                <div className="space-y-2 max-h-[150px] overflow-y-auto">
                                  {clientDetail.diet.meals?.map((meal, idx) => (
                                    <div key={idx} className="p-2 bg-[#121212] rounded-lg">
                                      <div className="flex justify-between items-center">
                                        <span className="text-white font-medium text-sm">{meal.name}</span>
                                        <span className="text-[#666] text-xs">{meal.time}</span>
                                      </div>
                                      <p className="text-[#888] text-xs mt-1 truncate">{meal.option_a}</p>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>

                            {/* Reset Button */}
                            <Button
                              variant="outline"
                              onClick={handleResetDiet}
                              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                              size="sm"
                            >
                              <RotateCcw className="w-3 h-3 mr-2" />
                              Resetear al Template Original
                            </Button>
                          </>
                        ) : (
                          <Card className="bg-orange-500/10 border-orange-500/30">
                            <CardContent className="p-4 text-center">
                              <AlertCircle className="w-10 h-10 text-orange-400 mx-auto mb-2" />
                              <h3 className="text-white font-semibold mb-1 text-sm">Sin dieta tradicional</h3>
                              <p className="text-[#888] text-xs mb-3">Asigna una dieta base para el modo tradicional.</p>
                              <Button
                                onClick={() => setShowDietDialog(true)}
                                className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
                                size="sm"
                              >
                                <Utensils className="w-3 h-3 mr-2" />
                                Asignar Dieta
                              </Button>
                            </CardContent>
                          </Card>
                        )}
                      </>
                    )}

                    {/* Flexible Diet Info - Only show if mode is flexible or hybrid */}
                    {(nutritionConfig.diet_mode === "flexible" || nutritionConfig.diet_mode === "hybrid") && (
                      <Card className="bg-green-500/10 border-green-500/30">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-green-400 font-semibold flex items-center gap-2">
                              <ChefHat className="w-4 h-4" />
                              Sistema Flexible Activo
                            </h3>
                            <Badge className="bg-green-500/20 text-green-400 border-none text-xs">
                              Recetas por Macros
                            </Badge>
                          </div>
                          
                          <p className="text-[#888] text-xs mb-3">
                            El cliente puede elegir recetas y el sistema calculará automáticamente los gramos de cada ingrediente para alcanzar sus macros objetivo.
                          </p>
                          
                          {/* Meal Slots Preview */}
                          {Object.keys(nutritionConfig.meal_slots || {}).length > 0 && (
                            <div className="space-y-2">
                              <p className="text-[#666] text-xs font-semibold">Distribución por comidas:</p>
                              <div className="grid grid-cols-5 gap-1">
                                {Object.entries(nutritionConfig.meal_slots).map(([slotId, slot]) => (
                                  <div key={slotId} className="p-2 bg-[#1A1A1A] rounded text-center">
                                    <p className="text-white text-[10px] font-medium truncate">{slot.name}</p>
                                    <p className="text-[#D4AF37] text-xs font-bold">{slot.targets?.calories || 0}</p>
                                    <p className="text-[#666] text-[8px]">kcal</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </TabsContent>

                  {/* CHECKINS TAB - Sistema de Check-ins Semanales */}
                  <TabsContent value="checkins" className="max-h-[500px] overflow-y-auto">
                    <ClientCheckInsView 
                      memberId={selectedClient.user.id}
                      memberName={clientDetail.profile?.nombre || clientDetail.user.email.split("@")[0]}
                    />
                  </TabsContent>

                  {/* FOTOS TAB */}
                  <TabsContent value="fotos" className="space-y-4">
                    {clientDetail.photos?.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {clientDetail.photos.map((photo) => (
                          <div key={photo.id} className="aspect-[3/4] rounded-lg overflow-hidden bg-[#1A1A1A]">
                            <img src={photo.url} alt={photo.tipo} className="w-full h-full object-cover" />
                            <Badge className="absolute bottom-1 left-1 bg-black/70 text-white text-xs">
                              {photo.tipo}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Camera className="w-12 h-12 text-[#333] mx-auto mb-3" />
                        <p className="text-[#666]">Sin fotos de progreso</p>
                      </div>
                    )}
                  </TabsContent>

                  {/* NOTAS TAB */}
                  <TabsContent value="notas" className="space-y-4">
                    <div>
                      <label className="text-white font-semibold mb-2 block">Notas del Coach</label>
                      <Textarea
                        value={coachNotes}
                        onChange={(e) => setCoachNotes(e.target.value)}
                        placeholder="Notas privadas sobre este cliente..."
                        className="bg-[#1A1A1A] border-[#2A2A2A] text-white min-h-[200px]"
                      />
                      <Button
                        onClick={handleSaveCoachNotes}
                        className="mt-3 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Notas
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      {/* Diet Assignment Dialog */}
      <Dialog open={showDietDialog} onOpenChange={setShowDietDialog}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Utensils className="w-5 h-5 text-[#D4AF37]" />
              Asignar Dieta Base
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Select value={selectedDietId} onValueChange={setSelectedDietId}>
              <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                <SelectValue placeholder="Seleccionar template..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                {diets.map((diet) => (
                  <SelectItem key={diet.id} value={diet.id} className="text-white hover:bg-[#2A2A2A]">
                    {diet.name} ({diet.calories_target} kcal)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedDietId && (
              <div className="p-3 bg-[#1A1A1A] rounded-lg">
                {(() => {
                  const diet = diets.find(d => d.id === selectedDietId);
                  if (!diet) return null;
                  return (
                    <>
                      <h4 className="text-white font-semibold">{diet.name}</h4>
                      <p className="text-[#888] text-sm">{diet.objetivo}</p>
                      <div className="flex gap-4 mt-2 text-xs">
                        <span className="text-white">{diet.calories_target} kcal</span>
                        <span className="text-red-400">{diet.protein_target}g prot</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setShowDietDialog(false)} className="border-[#2A2A2A] text-[#888]">
              Cancelar
            </Button>
            <Button onClick={handleAssignDiet} disabled={savingDiet || !selectedDietId} className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]">
              {savingDiet ? "Guardando..." : "Asignar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diet Editor Dialog */}
      <Dialog open={showDietEditor} onOpenChange={setShowDietEditor}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-[#D4AF37]" />
              Editar Dieta de {clientDetail?.profile?.nombre || "Cliente"}
            </DialogTitle>
          </DialogHeader>
          
          {editingDiet && (
            <div className="space-y-6">
              {/* Macros Targets */}
              <div>
                <h4 className="text-white font-semibold mb-3">Objetivos de Macros</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="text-[#888] text-xs">Calorías</label>
                    <Input
                      type="number"
                      value={editingDiet.calories_target}
                      onChange={(e) => setEditingDiet({...editingDiet, calories_target: parseInt(e.target.value) || 0})}
                      className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-[#888] text-xs">Proteína (g)</label>
                    <Input
                      type="number"
                      value={editingDiet.protein_target}
                      onChange={(e) => setEditingDiet({...editingDiet, protein_target: parseInt(e.target.value) || 0})}
                      className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-[#888] text-xs">Carbos (g)</label>
                    <Input
                      type="number"
                      value={editingDiet.carbs_target || ""}
                      onChange={(e) => setEditingDiet({...editingDiet, carbs_target: parseInt(e.target.value) || null})}
                      className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    />
                  </div>
                  <div>
                    <label className="text-[#888] text-xs">Grasas (g)</label>
                    <Input
                      type="number"
                      value={editingDiet.fat_target || ""}
                      onChange={(e) => setEditingDiet({...editingDiet, fat_target: parseInt(e.target.value) || null})}
                      className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Daily Totals Summary - REAL-TIME */}
              {editingDiet.meals?.length > 0 && (
                <div className="p-4 bg-gradient-to-br from-[#1A1A1A] to-[#0B0B0B] rounded-xl border border-[#2A2A2A]">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-[#D4AF37] font-bold flex items-center gap-2">
                      <Calculator className="w-5 h-5" />
                      TOTALES DIARIOS (calculado)
                    </h4>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Option A Totals */}
                    <div className="p-3 bg-[#D4AF37]/5 rounded-lg border border-[#D4AF37]/30">
                      <div className="text-xs text-[#D4AF37] font-bold mb-3 flex items-center gap-2">
                        <Flame className="w-4 h-4" /> OPCIÓN A
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[10px] text-[#666]">Calorías</div>
                          <div className="flex items-center gap-1">
                            <span className="text-[#D4AF37] font-bold text-lg">{dailyTotals.optionA.calories}</span>
                            {editingDiet.calories_target > 0 && (
                              <span className={cn(
                                "text-[10px]",
                                Math.abs(dailyTotals.optionA.calories - editingDiet.calories_target) <= 100
                                  ? "text-green-400"
                                  : dailyTotals.optionA.calories < editingDiet.calories_target
                                    ? "text-yellow-400"
                                    : "text-red-400"
                              )}>
                                ({dailyTotals.optionA.calories - editingDiet.calories_target > 0 ? "+" : ""}{dailyTotals.optionA.calories - editingDiet.calories_target})
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#666]">Proteína</div>
                          <div className="flex items-center gap-1">
                            <span className="text-red-400 font-bold">{dailyTotals.optionA.protein}g</span>
                            {editingDiet.protein_target > 0 && (
                              <span className={cn(
                                "text-[10px]",
                                dailyTotals.optionA.protein >= editingDiet.protein_target ? "text-green-400" : "text-yellow-400"
                              )}>
                                ({Math.round((dailyTotals.optionA.protein / editingDiet.protein_target) * 100)}%)
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#666]">Carbos</div>
                          <span className="text-yellow-400 font-bold">{dailyTotals.optionA.carbs}g</span>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#666]">Grasas</div>
                          <span className="text-blue-400 font-bold">{dailyTotals.optionA.fat}g</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Option B Totals */}
                    <div className="p-3 bg-[#888]/5 rounded-lg border border-[#888]/30">
                      <div className="text-xs text-[#888] font-bold mb-3 flex items-center gap-2">
                        <Flame className="w-4 h-4" /> OPCIÓN B
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <div className="text-[10px] text-[#666]">Calorías</div>
                          <div className="flex items-center gap-1">
                            <span className="text-[#888] font-bold text-lg">{dailyTotals.optionB.calories}</span>
                            {editingDiet.calories_target > 0 && dailyTotals.optionB.calories > 0 && (
                              <span className={cn(
                                "text-[10px]",
                                Math.abs(dailyTotals.optionB.calories - editingDiet.calories_target) <= 100
                                  ? "text-green-400"
                                  : dailyTotals.optionB.calories < editingDiet.calories_target
                                    ? "text-yellow-400"
                                    : "text-red-400"
                              )}>
                                ({dailyTotals.optionB.calories - editingDiet.calories_target > 0 ? "+" : ""}{dailyTotals.optionB.calories - editingDiet.calories_target})
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#666]">Proteína</div>
                          <div className="flex items-center gap-1">
                            <span className="text-red-400 font-bold">{dailyTotals.optionB.protein}g</span>
                            {editingDiet.protein_target > 0 && dailyTotals.optionB.protein > 0 && (
                              <span className={cn(
                                "text-[10px]",
                                dailyTotals.optionB.protein >= editingDiet.protein_target ? "text-green-400" : "text-yellow-400"
                              )}>
                                ({Math.round((dailyTotals.optionB.protein / editingDiet.protein_target) * 100)}%)
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#666]">Carbos</div>
                          <span className="text-yellow-400 font-bold">{dailyTotals.optionB.carbs}g</span>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#666]">Grasas</div>
                          <span className="text-blue-400 font-bold">{dailyTotals.optionB.fat}g</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Progress bar for calories */}
                  {editingDiet.calories_target > 0 && dailyTotals.optionA.calories > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-[#666]">Progreso Calorías (Opción A)</span>
                        <span className="text-[10px] text-[#888]">
                          {dailyTotals.optionA.calories} / {editingDiet.calories_target} kcal
                        </span>
                      </div>
                      <div className="h-2 bg-[#2A2A2A] rounded-full overflow-hidden">
                        <div 
                          className={cn(
                            "h-full transition-all duration-300",
                            Math.abs(dailyTotals.optionA.calories - editingDiet.calories_target) <= 100
                              ? "bg-green-500"
                              : dailyTotals.optionA.calories < editingDiet.calories_target
                                ? "bg-[#D4AF37]"
                                : "bg-red-500"
                          )}
                          style={{ width: `${Math.min((dailyTotals.optionA.calories / editingDiet.calories_target) * 100, 120)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Smart Macro Suggestions */}
              {editingDiet.meals?.length > 0 && (editingDiet.calories_target > 0 || editingDiet.protein_target > 0) && (
                <MacroSuggestions
                  targets={{
                    calories_target: editingDiet.calories_target,
                    protein_target: editingDiet.protein_target,
                    carbs_target: editingDiet.carbs_target,
                    fat_target: editingDiet.fat_target
                  }}
                  actuals={dailyTotals.optionA}
                  foodsLibrary={foodsLibrary}
                  currentMealItems={getAllMealItems}
                  onApplySuggestion={handleApplySuggestion}
                />
              )}

              {/* Meals */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-semibold">Comidas ({editingDiet.meals?.length || 0})</h4>
                  
                  {/* Templates dropdown */}
                  {mealTemplates.length > 0 && (
                    <Select onValueChange={(templateId) => {
                      const template = mealTemplates.find(t => t.id === templateId);
                      if (template) handleLoadTemplate(template);
                    }}>
                      <SelectTrigger className="w-[180px] h-8 bg-[#1A1A1A] border-[#2A2A2A] text-xs">
                        <Bookmark className="w-3 h-3 mr-1 text-[#D4AF37]" />
                        <SelectValue placeholder="Usar plantilla..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                        {mealTemplates.map(template => (
                          <SelectItem 
                            key={template.id} 
                            value={template.id}
                            className="text-white hover:bg-[#2A2A2A] text-xs"
                          >
                            <span>{template.name}</span>
                            <span className="text-[#666] ml-2">
                              ({template.macros_a?.calories || 0} kcal)
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                
                <div className="space-y-3">
                  {editingDiet.meals?.map((meal, idx) => (
                    <AdvancedMealEditor 
                      key={idx} 
                      meal={meal} 
                      onChange={(updated) => {
                        const newMeals = [...editingDiet.meals];
                        newMeals[idx] = updated;
                        setEditingDiet({...editingDiet, meals: newMeals});
                      }}
                      onDelete={() => {
                        const newMeals = editingDiet.meals.filter((_, i) => i !== idx);
                        setEditingDiet({...editingDiet, meals: newMeals});
                      }}
                      onDuplicate={() => handleDuplicateMeal(idx)}
                      onSaveAsTemplate={() => {
                        setSavingMealAsTemplate(meal);
                        setTemplateName(meal.name || "Nueva plantilla");
                        setShowTemplateDialog(true);
                      }}
                    />
                  ))}
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingDiet({
                        ...editingDiet,
                        meals: [...(editingDiet.meals || []), { 
                          name: "Nueva comida", 
                          time: "12:00", 
                          items_a: [], 
                          items_b: [], 
                          notes: "" 
                        }]
                      });
                    }}
                    className="w-full border-dashed border-[#2A2A2A] text-[#888]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Añadir Comida
                  </Button>
                </div>
              </div>

              {/* Additional Fields */}
              <div className="space-y-4">
                <div>
                  <label className="text-[#888] text-xs">Sustituciones</label>
                  <Textarea
                    value={editingDiet.sustituciones}
                    onChange={(e) => setEditingDiet({...editingDiet, sustituciones: e.target.value})}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs">Suplementación</label>
                  <Textarea
                    value={editingDiet.suplementacion}
                    onChange={(e) => setEditingDiet({...editingDiet, suplementacion: e.target.value})}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs">Notas GOATGYM</label>
                  <Textarea
                    value={editingDiet.notas_goatgym}
                    onChange={(e) => setEditingDiet({...editingDiet, notas_goatgym: e.target.value})}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs">Ajustes si estancamiento</label>
                  <Textarea
                    value={editingDiet.ajustes_estancamiento}
                    onChange={(e) => setEditingDiet({...editingDiet, ajustes_estancamiento: e.target.value})}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    rows={2}
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs">Notas privadas del coach</label>
                  <Textarea
                    value={editingDiet.coach_notes}
                    onChange={(e) => setEditingDiet({...editingDiet, coach_notes: e.target.value})}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                    rows={2}
                    placeholder="Notas solo visibles para ti..."
                  />
                </div>
              </div>

              {/* History note (optional) */}
              <div className="p-3 bg-[#0B0B0B] rounded-lg border border-[#2A2A2A]">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[#888] text-xs flex items-center gap-2">
                    <History className="w-3 h-3" />
                    Nota para el historial (opcional)
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHistory(true)}
                    className="h-6 px-2 text-[10px] text-[#666] hover:text-[#D4AF37]"
                  >
                    Ver historial
                  </Button>
                </div>
                <Input
                  value={historyNote}
                  onChange={(e) => setHistoryNote(e.target.value)}
                  className="bg-[#1A1A1A] border-[#2A2A2A] text-white text-sm"
                  placeholder="Ej: Subimos carbohidratos por fatiga en entrenamientos..."
                />
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t border-[#2A2A2A]">
                <Button variant="outline" onClick={() => setShowDietEditor(false)} className="border-[#2A2A2A] text-[#888]">
                  Cancelar
                </Button>
                <Button onClick={handleSaveClientDiet} disabled={savingDiet} className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]">
                  {savingDiet ? "Guardando..." : "Guardar Cambios"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diet History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de {selectedClient?.user?.name || 'Cliente'}
            </DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <DietHistory memberId={selectedClient.user.id} />
          )}
        </DialogContent>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2">
              <BookmarkPlus className="w-5 h-5" />
              Guardar como Plantilla
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[#888] text-xs block mb-1">Nombre de la plantilla</label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Ej: Desayuno definición estándar"
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
              />
            </div>
            
            {savingMealAsTemplate && (
              <div className="p-3 bg-[#1A1A1A] rounded-lg">
                <div className="text-xs text-[#888] mb-2">Vista previa:</div>
                <div className="text-sm text-white font-medium">{savingMealAsTemplate.name}</div>
                <div className="flex items-center gap-2 text-xs mt-1">
                  <span className="text-[#D4AF37]">
                    {calculateTotalMacros(savingMealAsTemplate.items_a || [], foodsLibrary).calories} kcal (A)
                  </span>
                  <span className="text-[#888]">
                    {(savingMealAsTemplate.items_a || []).length} items
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowTemplateDialog(false);
                  setSavingMealAsTemplate(null);
                  setTemplateName("");
                }}
                className="flex-1 border-[#2A2A2A] text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveMealAsTemplate}
                disabled={!templateName.trim()}
                className="flex-1 bg-[#D4AF37] hover:bg-[#B8962E] text-[#0B0B0B]"
              >
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Nutrition Config Editor Dialog */}
      <Dialog open={showNutritionConfigEditor} onOpenChange={setShowNutritionConfigEditor}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              Configuración Nutricional
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Diet Mode Selector */}
            <div>
              <label className="text-white font-semibold text-sm block mb-2">Tipo de Sistema</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: "traditional", label: "Tradicional", icon: Utensils, color: "blue" },
                  { id: "flexible", label: "Flexible", icon: ChefHat, color: "green" },
                  { id: "hybrid", label: "Híbrido", icon: Salad, color: "purple" }
                ].map(({ id, label, icon: Icon, color }) => (
                  <button
                    key={id}
                    onClick={() => setNutritionConfig({ ...nutritionConfig, diet_mode: id })}
                    className={cn(
                      "p-3 rounded-lg border-2 transition-all text-center",
                      nutritionConfig.diet_mode === id
                        ? `border-${color}-500 bg-${color}-500/10`
                        : "border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#3A3A3A]"
                    )}
                    style={{
                      borderColor: nutritionConfig.diet_mode === id 
                        ? (color === "blue" ? "#3b82f6" : color === "green" ? "#22c55e" : "#a855f7")
                        : undefined,
                      backgroundColor: nutritionConfig.diet_mode === id
                        ? (color === "blue" ? "rgba(59,130,246,0.1)" : color === "green" ? "rgba(34,197,94,0.1)" : "rgba(168,85,247,0.1)")
                        : undefined
                    }}
                  >
                    <Icon className={cn(
                      "w-5 h-5 mx-auto mb-1",
                      nutritionConfig.diet_mode === id 
                        ? (color === "blue" ? "text-blue-400" : color === "green" ? "text-green-400" : "text-purple-400")
                        : "text-[#666]"
                    )} />
                    <span className={cn(
                      "text-xs font-medium",
                      nutritionConfig.diet_mode === id ? "text-white" : "text-[#888]"
                    )}>
                      {label}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-[#666] text-xs mt-2">
                {nutritionConfig.diet_mode === "traditional" && "El coach define comidas fijas. El cliente sigue el plan exacto."}
                {nutritionConfig.diet_mode === "flexible" && "El cliente elige recetas. El sistema calcula gramos automáticamente."}
                {nutritionConfig.diet_mode === "hybrid" && "Combina comidas fijas con slots flexibles para mayor variedad."}
              </p>
            </div>

            {/* Objetivo Selector */}
            <div>
              <label className="text-white font-semibold text-sm block mb-2">Objetivo del Cliente</label>
              <Select 
                value={nutritionConfig.objetivo} 
                onValueChange={(v) => setNutritionConfig({ ...nutritionConfig, objetivo: v })}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
                  <SelectValue placeholder="Seleccionar objetivo..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  <SelectItem value="definicion" className="text-white hover:bg-[#2A2A2A]">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-400" />
                      Definición (Pérdida de grasa)
                    </div>
                  </SelectItem>
                  <SelectItem value="recomposicion" className="text-white hover:bg-[#2A2A2A]">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-yellow-400" />
                      Recomposición (Mantener/mejorar)
                    </div>
                  </SelectItem>
                  <SelectItem value="musculacion" className="text-white hover:bg-[#2A2A2A]">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      Musculación (Ganancia muscular)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[#666] text-xs mt-1">
                El objetivo filtra las recetas disponibles en modo flexible.
              </p>
            </div>

            {/* Daily Macros */}
            <div>
              <label className="text-white font-semibold text-sm block mb-2">Macros Diarios Objetivo</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[#888] text-xs">Calorías</label>
                  <Input
                    type="number"
                    value={nutritionConfig.daily_targets?.calories || ""}
                    onChange={(e) => {
                      const newTargets = {
                        ...nutritionConfig.daily_targets,
                        calories: parseInt(e.target.value) || 0
                      };
                      setNutritionConfig({
                        ...nutritionConfig,
                        daily_targets: newTargets,
                        meal_slots: nutritionConfig.diet_mode !== "traditional" 
                          ? recalculateMealSlots(newTargets) 
                          : nutritionConfig.meal_slots
                      });
                    }}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs">Proteína (g)</label>
                  <Input
                    type="number"
                    value={nutritionConfig.daily_targets?.protein || ""}
                    onChange={(e) => {
                      const newTargets = {
                        ...nutritionConfig.daily_targets,
                        protein: parseInt(e.target.value) || 0
                      };
                      setNutritionConfig({
                        ...nutritionConfig,
                        daily_targets: newTargets,
                        meal_slots: nutritionConfig.diet_mode !== "traditional"
                          ? recalculateMealSlots(newTargets)
                          : nutritionConfig.meal_slots
                      });
                    }}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs">Carbohidratos (g)</label>
                  <Input
                    type="number"
                    value={nutritionConfig.daily_targets?.carbs || ""}
                    onChange={(e) => {
                      const newTargets = {
                        ...nutritionConfig.daily_targets,
                        carbs: parseInt(e.target.value) || 0
                      };
                      setNutritionConfig({
                        ...nutritionConfig,
                        daily_targets: newTargets,
                        meal_slots: nutritionConfig.diet_mode !== "traditional"
                          ? recalculateMealSlots(newTargets)
                          : nutritionConfig.meal_slots
                      });
                    }}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                  />
                </div>
                <div>
                  <label className="text-[#888] text-xs">Grasas (g)</label>
                  <Input
                    type="number"
                    value={nutritionConfig.daily_targets?.fat || ""}
                    onChange={(e) => {
                      const newTargets = {
                        ...nutritionConfig.daily_targets,
                        fat: parseInt(e.target.value) || 0
                      };
                      setNutritionConfig({
                        ...nutritionConfig,
                        daily_targets: newTargets,
                        meal_slots: nutritionConfig.diet_mode !== "traditional"
                          ? recalculateMealSlots(newTargets)
                          : nutritionConfig.meal_slots
                      });
                    }}
                    className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                  />
                </div>
              </div>
            </div>

            {/* Meal Slots Configuration (for flexible/hybrid) */}
            {nutritionConfig.diet_mode !== "traditional" && (
              <div className="space-y-3">
                {/* Plantillas Rápidas */}
                <div>
                  <label className="text-white font-semibold text-sm block mb-2">Plantilla de Comidas</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[4, 5, 6].map((num) => (
                      <button
                        key={num}
                        onClick={() => applyMealTemplate(num)}
                        className={cn(
                          "p-2 rounded-lg border-2 transition-all text-center",
                          selectedTemplate === num
                            ? "border-[#D4AF37] bg-[#D4AF37]/10"
                            : "border-[#2A2A2A] bg-[#1A1A1A] hover:border-[#3A3A3A]"
                        )}
                      >
                        <span className={cn(
                          "text-sm font-bold",
                          selectedTemplate === num ? "text-[#D4AF37]" : "text-white"
                        )}>
                          {num}
                        </span>
                        <span className="text-[#888] text-xs block">comidas</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Distribución Personalizada */}
                <div className="p-3 bg-[#1A1A1A] rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-white text-sm font-semibold flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-[#D4AF37]" />
                      Distribución por Comida
                    </h4>
                    <div className={cn(
                      "px-2 py-1 rounded text-xs font-bold",
                      totalPercent === 100 
                        ? "bg-green-500/20 text-green-400" 
                        : "bg-red-500/20 text-red-400"
                    )}>
                      Total: {totalPercent}%
                    </div>
                  </div>
                  
                  {totalPercent !== 100 && (
                    <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-xs flex items-center gap-2">
                        <AlertCircle className="w-3 h-3" />
                        Los porcentajes deben sumar exactamente 100%
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    {Object.entries(mealDistribution).map(([slotId, config]) => {
                      const pct = config.percent / 100;
                      const slotCals = Math.round((nutritionConfig.daily_targets?.calories || 0) * pct);
                      const slotProt = Math.round((nutritionConfig.daily_targets?.protein || 0) * pct);
                      const slotCarbs = Math.round((nutritionConfig.daily_targets?.carbs || 0) * pct);
                      const slotFat = Math.round((nutritionConfig.daily_targets?.fat || 0) * pct);
                      
                      return (
                        <div key={slotId} className="p-2 bg-[#121212] rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Input
                              value={config.name}
                              onChange={(e) => updateMealName(slotId, e.target.value)}
                              className="flex-1 h-8 bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm"
                              placeholder="Nombre comida"
                            />
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={config.percent}
                                onChange={(e) => updateMealPercent(slotId, e.target.value)}
                                className="w-14 h-8 bg-[#0B0B0B] border-[#2A2A2A] text-white text-sm text-center"
                                min="0"
                                max="100"
                              />
                              <span className="text-[#666] text-xs">%</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeMealSlot(slotId)}
                              className="h-8 w-8 p-0 text-[#666] hover:text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-3 text-[10px]">
                            <span className="text-[#D4AF37] font-bold">{slotCals} kcal</span>
                            <span className="text-red-400">{slotProt}g P</span>
                            <span className="text-yellow-400">{slotCarbs}g C</span>
                            <span className="text-blue-400">{slotFat}g G</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addMealSlot}
                    className="w-full mt-2 border-[#2A2A2A] text-[#888] hover:text-white hover:border-[#D4AF37]"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Añadir comida
                  </Button>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowNutritionConfigEditor(false)}
                className="flex-1 border-[#2A2A2A] text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveNutritionConfig}
                disabled={savingNutritionConfig || (nutritionConfig.diet_mode !== "traditional" && totalPercent !== 100)}
                className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] disabled:opacity-50"
              >
                {savingNutritionConfig ? "Guardando..." : "Guardar Configuración"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Aplicar Plantilla GOAT */}
      <Dialog open={showApplyTemplate} onOpenChange={setShowApplyTemplate}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2">
              <Library className="w-5 h-5" />
              Aplicar Plantilla Nutricional
            </DialogTitle>
          </DialogHeader>
          
          <p className="text-[#888] text-sm mb-4">
            Selecciona una plantilla para aplicar a {selectedClient?.user?.name || selectedClient?.user?.email}
          </p>
          
          <div className="space-y-2">
            {nutritionTemplates.length === 0 ? (
              <div className="text-center py-8 text-[#666]">
                <Library className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No hay plantillas disponibles</p>
                <Button
                  variant="link"
                  onClick={() => navigate("/admin/goat-library")}
                  className="text-[#D4AF37] mt-2"
                >
                  Ir a Biblioteca GOAT
                </Button>
              </div>
            ) : (
              nutritionTemplates.map((template) => (
                <Card 
                  key={template.id}
                  className="bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#D4AF37]/50 cursor-pointer transition-all"
                  onClick={() => applyNutritionTemplate(template.id)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-white font-semibold text-sm">{template.name}</h4>
                        <div className="flex gap-2 mt-1">
                          <Badge className={cn(
                            "border-none text-[10px]",
                            template.gender === "hombre" ? "bg-blue-500/20 text-blue-400" : "bg-pink-500/20 text-pink-400"
                          )}>
                            {template.gender}
                          </Badge>
                          <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-none text-[10px]">
                            {template.objective}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[#D4AF37] font-bold">{template.daily_targets?.calories} kcal</p>
                        <p className="text-[#888] text-xs">{template.daily_targets?.protein}g P</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
          
          {applyingTemplate && (
            <div className="flex items-center justify-center py-4 text-[#D4AF37]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Aplicando plantilla...
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* =============================================
          GOAT WIZARD - Configuración completa rápida
          Entrenamiento + Nutrición en < 30 segundos
          ============================================= */}
      <Dialog open={showGoatWizard} onOpenChange={setShowGoatWizard}>
        <DialogContent className="bg-[#121212] border-[#2A2A2A] text-white max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37] flex items-center gap-2">
              <Library className="w-5 h-5" />
              Plantilla GOAT - Configuración Rápida
            </DialogTitle>
            <p className="text-[#888] text-sm">
              Configura a {selectedClient?.user?.name || selectedClient?.user?.email?.split("@")[0]} en segundos
            </p>
          </DialogHeader>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-between px-4 py-2 bg-[#1A1A1A] rounded-lg mb-4">
            <div className={cn(
              "flex items-center gap-2",
              goatWizardStep >= 1 ? "text-[#D4AF37]" : "text-[#666]"
            )}>
              <div className={cn(
                "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                goatWizardStep >= 1 ? "bg-[#D4AF37] text-black" : "bg-[#333] text-[#666]"
              )}>1</div>
              <span className="text-xs hidden sm:inline">Entreno</span>
            </div>
            <div className="flex-1 h-0.5 bg-[#333] mx-2">
              <div className={cn("h-full bg-[#D4AF37] transition-all", goatWizardStep >= 2 ? "w-full" : "w-0")} />
            </div>
            <div className={cn(
              "flex items-center gap-2",
              goatWizardStep >= 2 ? "text-[#D4AF37]" : "text-[#666]"
            )}>
              <div className={cn(
                "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                goatWizardStep >= 2 ? "bg-[#D4AF37] text-black" : "bg-[#333] text-[#666]"
              )}>2</div>
              <span className="text-xs hidden sm:inline">Nutrición</span>
            </div>
            <div className="flex-1 h-0.5 bg-[#333] mx-2">
              <div className={cn("h-full bg-[#D4AF37] transition-all", goatWizardStep >= 3 ? "w-full" : "w-0")} />
            </div>
            <div className={cn(
              "flex items-center gap-2",
              goatWizardStep >= 3 ? "text-[#D4AF37]" : "text-[#666]"
            )}>
              <div className={cn(
                "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                goatWizardStep >= 3 ? "bg-[#D4AF37] text-black" : "bg-[#333] text-[#666]"
              )}>3</div>
              <span className="text-xs hidden sm:inline">Aplicar</span>
            </div>
          </div>

          {/* Step Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Step 1: Seleccionar Entrenamiento */}
            {goatWizardStep === 1 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Dumbbell className="w-4 h-4 text-[#D4AF37]" />
                    Selecciona Plantilla de Entrenamiento
                  </h3>
                  <Badge className="bg-[#2A2A2A] text-[#888] border-none text-xs">Opcional</Badge>
                </div>
                
                {workoutTemplates.length === 0 ? (
                  <div className="text-center py-6 text-[#666]">
                    <Dumbbell className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No hay plantillas de entrenamiento</p>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => navigate("/admin/goat-library")}
                      className="text-[#D4AF37] mt-1"
                    >
                      Crear en Biblioteca GOAT
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
                    {workoutTemplates.map((template) => (
                      <Card 
                        key={template.id}
                        className={cn(
                          "cursor-pointer transition-all",
                          selectedWorkoutTemplate?.id === template.id
                            ? "bg-[#D4AF37]/20 border-[#D4AF37]"
                            : "bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#D4AF37]/50"
                        )}
                        onClick={() => setSelectedWorkoutTemplate(
                          selectedWorkoutTemplate?.id === template.id ? null : template
                        )}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                                {selectedWorkoutTemplate?.id === template.id && (
                                  <Check className="w-4 h-4 text-[#D4AF37]" />
                                )}
                                {template.name}
                              </h4>
                              <div className="flex gap-2 mt-1">
                                <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-none text-[10px]">
                                  {template.objective || "General"}
                                </Badge>
                                {template.days_per_week && (
                                  <Badge className="bg-[#2A2A2A] text-[#888] border-none text-[10px]">
                                    {template.days_per_week} días/sem
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <Dumbbell className="w-5 h-5 text-[#D4AF37]/50" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Seleccionar Nutrición */}
            {goatWizardStep === 2 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Utensils className="w-4 h-4 text-[#D4AF37]" />
                    Selecciona Plantilla de Nutrición
                  </h3>
                  <Badge className="bg-[#2A2A2A] text-[#888] border-none text-xs">Opcional</Badge>
                </div>

                {/* Filtros rápidos */}
                <div className="flex gap-2 mb-3">
                  <select
                    value={wizardGenderFilter}
                    className="px-2 py-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-white text-xs"
                    onChange={(e) => setWizardGenderFilter(e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="hombre">Hombre</option>
                    <option value="mujer">Mujer</option>
                  </select>
                  <select
                    value={wizardObjectiveFilter}
                    className="px-2 py-1 bg-[#1A1A1A] border border-[#2A2A2A] rounded text-white text-xs"
                    onChange={(e) => setWizardObjectiveFilter(e.target.value)}
                  >
                    <option value="">Todos objetivos</option>
                    <option value="definicion">Definición</option>
                    <option value="recomposicion">Recomposición</option>
                    <option value="volumen">Volumen</option>
                  </select>
                </div>
                
                {nutritionTemplates.length === 0 ? (
                  <div className="text-center py-6 text-[#666]">
                    <Utensils className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No hay plantillas nutricionales</p>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => navigate("/admin/goat-library")}
                      className="text-[#D4AF37] mt-1"
                    >
                      Crear en Biblioteca GOAT
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
                    {nutritionTemplates
                      .filter(t => {
                        if (wizardGenderFilter && t.gender !== wizardGenderFilter) return false;
                        if (wizardObjectiveFilter && t.objective !== wizardObjectiveFilter) return false;
                        return true;
                      })
                      .map((template) => (
                      <Card 
                        key={template.id}
                        className={cn(
                          "cursor-pointer transition-all",
                          selectedNutritionTemplate?.id === template.id
                            ? "bg-[#D4AF37]/20 border-[#D4AF37]"
                            : "bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#D4AF37]/50"
                        )}
                        onClick={() => setSelectedNutritionTemplate(
                          selectedNutritionTemplate?.id === template.id ? null : template
                        )}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-white font-semibold text-sm flex items-center gap-2">
                                {selectedNutritionTemplate?.id === template.id && (
                                  <Check className="w-4 h-4 text-[#D4AF37]" />
                                )}
                                {template.name}
                              </h4>
                              <div className="flex gap-2 mt-1">
                                <Badge className={cn(
                                  "border-none text-[10px]",
                                  template.gender === "hombre" ? "bg-blue-500/20 text-blue-400" : "bg-pink-500/20 text-pink-400"
                                )}>
                                  {template.gender}
                                </Badge>
                                <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-none text-[10px]">
                                  {template.objective}
                                </Badge>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-[#D4AF37] font-bold text-sm">{template.daily_targets?.calories}</p>
                              <p className="text-[#666] text-[10px]">kcal</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Resumen y Confirmar */}
            {goatWizardStep === 3 && (
              <div className="space-y-4">
                <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                  <Target className="w-4 h-4 text-[#D4AF37]" />
                  Resumen de Configuración
                </h3>

                {/* Cliente */}
                <div className="p-3 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                      <User className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">
                        {selectedClient?.user?.name || selectedClient?.user?.email?.split("@")[0]}
                      </p>
                      <p className="text-[#666] text-xs">{selectedClient?.user?.email}</p>
                    </div>
                  </div>
                </div>

                {/* Entrenamiento seleccionado */}
                <div className="p-3 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Dumbbell className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-[#888] text-sm">Entrenamiento</span>
                    </div>
                    {selectedWorkoutTemplate ? (
                      <div className="text-right">
                        <p className="text-white font-semibold text-sm">{selectedWorkoutTemplate.name}</p>
                        <p className="text-[#666] text-xs">{selectedWorkoutTemplate.days_per_week} días/sem</p>
                      </div>
                    ) : (
                      <Badge className="bg-[#333] text-[#666] border-none text-xs">Sin cambios</Badge>
                    )}
                  </div>
                </div>

                {/* Nutrición seleccionada */}
                <div className="p-3 bg-[#1A1A1A] rounded-lg border border-[#2A2A2A]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Utensils className="w-4 h-4 text-[#D4AF37]" />
                      <span className="text-[#888] text-sm">Nutrición</span>
                    </div>
                    {selectedNutritionTemplate ? (
                      <div className="text-right">
                        <p className="text-white font-semibold text-sm">{selectedNutritionTemplate.name}</p>
                        <p className="text-[#666] text-xs">
                          {selectedNutritionTemplate.daily_targets?.calories} kcal • {selectedNutritionTemplate.daily_targets?.protein}g P
                        </p>
                      </div>
                    ) : (
                      <Badge className="bg-[#333] text-[#666] border-none text-xs">Sin cambios</Badge>
                    )}
                  </div>
                </div>

                {/* Warning si no hay selección */}
                {!selectedWorkoutTemplate && !selectedNutritionTemplate && (
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-400 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      No has seleccionado ninguna plantilla
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-3 pt-4 border-t border-[#2A2A2A] mt-4">
            {goatWizardStep > 1 && (
              <Button
                variant="outline"
                onClick={() => setGoatWizardStep(goatWizardStep - 1)}
                className="flex-1 border-[#2A2A2A] text-white"
                disabled={applyingGoat}
              >
                Atrás
              </Button>
            )}
            
            {goatWizardStep < 3 ? (
              <Button
                onClick={() => setGoatWizardStep(goatWizardStep + 1)}
                className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-black font-bold"
              >
                {goatWizardStep === 1 ? "Siguiente: Nutrición" : "Revisar"}
              </Button>
            ) : (
              <Button
                onClick={applyGoatConfig}
                disabled={applyingGoat || (!selectedWorkoutTemplate && !selectedNutritionTemplate)}
                className="flex-1 bg-gradient-to-r from-[#D4AF37] to-[#B8960C] hover:from-[#E5C54D] hover:to-[#D4AF37] text-black font-bold"
              >
                {applyingGoat ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Aplicando...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    Aplicar Configuración
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Helper Components
const IntakeSection = ({ title, icon: Icon, children }) => (
  <div className="p-3 bg-[#1A1A1A] rounded-lg">
    <h4 className="text-[#D4AF37] font-semibold text-sm flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4" />
      {title}
    </h4>
    <div className="grid grid-cols-2 gap-2">
      {children}
    </div>
  </div>
);

const IntakeField = ({ label, value, highlight, fullWidth }) => (
  <div className={cn("p-2 bg-[#121212] rounded", fullWidth && "col-span-2")}>
    <p className="text-[#666] text-xs">{label}</p>
    <p className={cn("text-sm font-medium", highlight ? "text-[#D4AF37]" : "text-white")}>
      {value || "-"}
    </p>
  </div>
);

// Format helpers
const formatGoal = (goal) => {
  const goals = {
    perdida_grasa: "Pérdida de Grasa",
    recomposicion: "Recomposición",
    ganancia_muscular: "Ganancia Muscular"
  };
  return goals[goal] || goal || "-";
};

const formatExperience = (level) => {
  const levels = {
    principiante: "Principiante",
    intermedio: "Intermedio",
    avanzado: "Avanzado"
  };
  return levels[level] || level || "-";
};

const formatEquipment = (equip) => {
  const equipment = {
    gimnasio_completo: "Gimnasio Completo",
    casa_basico: "Casa - Básico",
    casa_completo: "Casa - Completo"
  };
  return equipment[equip] || equip || "-";
};

const formatMealSchedule = (schedule) => {
  const schedules = {
    "3_comidas": "3 comidas",
    "4_comidas": "4 comidas",
    "5_comidas": "5 comidas"
  };
  return schedules[schedule] || schedule || "-";
};

const formatActivity = (activity) => {
  const activities = {
    sedentario: "Sedentario",
    ligero: "Ligero",
    moderado: "Moderado",
    activo: "Activo",
    muy_activo: "Muy Activo"
  };
  return activities[activity] || activity || "-";
};

const formatWork = (work) => {
  const works = {
    oficina: "Oficina",
    fisico: "Trabajo Físico",
    mixto: "Mixto",
    remoto: "Remoto"
  };
  return works[work] || work || "-";
};

export default AdminPremiumClients;
